import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, LoginData, AuthResponse } from '../types';
import axiosClient from '../config/axios';
import { saveSession, getDeviceProfile, verifyPin } from '../utils/deviceAuth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginData) => Promise<void>;
  logout: () => void;
  /** Restaura la sesión offline validando el PIN del dispositivo. */
  unlockOffline: (pin: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (data: LoginData) => {
    try {
      const response = await axiosClient.post('/login', data);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      saveSession(user, token);   // perfil de dispositivo para entrar offline
      setUser(user);
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  /** Entrar sin internet: valida el PIN y restaura la sesión guardada. */
  const unlockOffline = async (pin: string): Promise<boolean> => {
    const ok = await verifyPin(pin);
    if (!ok) return false;
    const profile = getDeviceProfile();
    if (!profile) return false;
    localStorage.setItem('token', profile.token);
    localStorage.setItem('user', JSON.stringify(profile.user));
    setUser(profile.user);
    return true;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Se CONSERVA el perfil de dispositivo + PIN a propósito: así, sin internet,
    // pueden volver a entrar con el PIN. Para desenrolar el equipo se usa
    // clearDeviceProfile() aparte (no en un logout normal).
    setUser(null);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    unlockOffline
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// HOC para proteger rutas
export const withAuth = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredRole?: 'administrador' | 'vendedor'
) => {
  return (props: P) => {
    const { user, isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return <div>Cargando...</div>;
    }

    if (!isAuthenticated) {
      window.location.href = '/login';
      return null;
    }

    if (requiredRole && user?.role !== requiredRole) {
      return <div>No tienes permisos para acceder a esta página</div>;
    }

    return <WrappedComponent {...props} />;
  };
};
